import { TourSlide } from "../types";

/**
 * The Executive Assistant deck for the client portal release.
 *
 * The id is dated because it is a permanent key in `announcementTour.seenCampaigns` — a future
 * announcement must never reuse it, or everyone who dismissed this one silently skips that one.
 */
export const EA_PORTAL_REFRESH_CAMPAIGN_ID = "ea-portal-refresh-2026-09";

/**
 * Copy is the approved design, word for word. It is written for an assistant who used the old portal
 * ("we've updated your account", "nothing was lost in the redesign"), and every assistant sees it —
 * new or returning — because there is no per-account signal for who used the old one: the profile
 * payload carries no creation date.
 *
 * That is a deliberate, accepted trade, not an oversight. If it ever needs fixing, the fix is
 * server-side eligibility on the announcement payload — the operator record already carries an
 * `announcement` block — rather than an edit here.
 */
export const EA_PORTAL_REFRESH_SLIDES: TourSlide[] = [
  {
    eyebrow: "A fresh new look",
    title: "Your Assistant portal, redesigned",
    blocks: [
      {
        type: "callout",
        text: "You're an Executive Assistant now. Since you manage bookings for someone else, we've updated your account.",
      },
      {
        type: "paragraph",
        text: "We've given your portal a cleaner, simpler home for your clients, their bookings, and your own trips — all in one account.",
      },
      {
        // No descriptions: these render as bare ticked lines, per the design.
        type: "checklist",
        items: [
          { title: "Every booking across every client you manage, compiled into a single table." },
          { title: "Request access, then book on a client's behalf or update their settings for them." },
          { title: "You can still book and track your own trips as a passenger, separately." },
        ],
      },
    ],
  },
  {
    eyebrow: "Getting around",
    title: "Your new menu, top to bottom",
    blocks: [
      {
        type: "numbered",
        items: [
          {
            title: "All Bookings",
            description: "Top of the menu. One table for every booking across all your clients — search by Job ID, filter by date, or export.",
          },
          {
            title: "My Clients",
            description: "Request access to new clients, track pending requests, then open a client to view details or Manage Client to act for them.",
          },
          {
            title: "My Profile",
            description: "Your own details and your own booking history — the trips you take as a passenger.",
          },
        ],
      },
    ],
  },
  {
    eyebrow: "One last thing",
    title: "See how it all fits together",
    blocks: [
      {
        /*
         * Looks like a player, isn't one. The poster is a plain link out to the hosted video — no
         * embed, no iframe, so nothing to load or block inside the modal.
         */
        type: "video",
        title: "Take the tour",
        subtitle: "Managing your clients and their bookings, start to finish",
        // `duration` deliberately unset. The design shows 2:04, but this poster is a link out rather
        // than a player, and a runtime we'd have to keep in sync by hand is a promise not worth making.
        href: "https://rideminder.vids.io/videos/dc9adcbb1b17e1c556/ea-web-app-refresh",
      },
    ],
    footnote: "Prefer to dive in? Everything lives under All Bookings, My Clients and Settings — nothing was lost in the redesign.",
  },
];
